import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { doc, getDoc, setDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useCompany } from './CompanyContext';
import { sanitizeForFirestore } from '../utils/firestoreUtils';

const SettingsContext = createContext();

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

const DEFAULT_PIPELINE_STAGES = ['New Lead', 'In Progress', 'Quoted', 'Approved', 'Booked', 'Archived'];
const ARCHIVED_STAGE = 'Archived';

export const SettingsProvider = ({ children }) => {
  const { companyId } = useCompany();
  const [pipelineStages, setPipelineStages] = useState(DEFAULT_PIPELINE_STAGES);
  const [isSettingsLoading, setIsSettingsLoading] = useState(true);

  const withArchived = (stages = []) => {
    const base = stages.filter((s) => s && s !== ARCHIVED_STAGE);
    return [...base, ARCHIVED_STAGE];
  };

  const stripArchived = (stages = []) => stages.filter((s) => s && s !== ARCHIVED_STAGE);

  // Ensure pipeline stages document exists
  const ensureStageDocExists = useCallback(async () => {
    if (!companyId) return;

    try {
      // Company-scoped pipeline stages
      const stagesDocRef = doc(db, 'companies', companyId, 'pipelineStages', 'default');
      const stagesDoc = await getDoc(stagesDocRef);

      if (!stagesDoc.exists()) {
        // Create default pipeline stages (without Archived; appended in client)
        await setDoc(
          stagesDocRef,
          sanitizeForFirestore({
            stages: DEFAULT_PIPELINE_STAGES,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          }),
        );
      }
    } catch (error) {
      console.error('Error ensuring stage doc exists:', error);
    }
  }, [companyId]);

  // Listen to pipeline stages (global default; can be extended per-location later)
  useEffect(() => {
    if (!db || !companyId) {
      // Not ready yet; AppReadyGate will prevent this provider from mounting.
      return;
    }

    // Ensure doc exists first
    ensureStageDocExists();

    const stagesDocRef = doc(db, 'companies', companyId, 'pipelineStages', 'default');
    const unsubscribe = onSnapshot(
      stagesDocRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setPipelineStages(withArchived(data.stages || DEFAULT_PIPELINE_STAGES));
        } else {
          setPipelineStages(withArchived(DEFAULT_PIPELINE_STAGES));
        }
        setIsSettingsLoading(false);
      },
      (error) => {
        console.error('Pipeline stages snapshot error:', error);
        setPipelineStages(withArchived(DEFAULT_PIPELINE_STAGES));
        setIsSettingsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [companyId, ensureStageDocExists]);

  // Update pipeline stages (non-archived only persisted)
  const updatePipelineStages = async (newStages) => {
    if (!companyId) {
      throw new Error('Missing companyId');
    }

    try {
      const stagesDocRef = doc(db, 'companies', companyId, 'pipelineStages', 'default');
      await setDoc(
        stagesDocRef,
        sanitizeForFirestore({
          stages: stripArchived(newStages),
          updatedAt: serverTimestamp(),
        }),
        { merge: true }
      );
    } catch (error) {
      console.error('Error updating pipeline stages:', error);
      throw error;
    }
  };

  // Stage management helpers (for Settings UI)
  const addStage = async (name) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const previous = pipelineStages;
    const current = pipelineStages.filter((s) => s && s !== ARCHIVED_STAGE);
    if (current.includes(trimmed) || trimmed === ARCHIVED_STAGE) return;
    const next = [...current, trimmed, ARCHIVED_STAGE];
    setPipelineStages(next);
    try {
      await updatePipelineStages(next);
    } catch (error) {
      setPipelineStages(previous);
      throw error;
    }
  };

  const renameStage = async (oldName, newName) => {
    const trimmed = newName.trim();
    if (!trimmed || oldName === ARCHIVED_STAGE || trimmed === ARCHIVED_STAGE) return;
    const previous = pipelineStages;
    const current = pipelineStages.filter((s) => s && s !== ARCHIVED_STAGE);
    const next = current.map((s) => (s === oldName ? trimmed : s));
    const withArch = [...next, ARCHIVED_STAGE];
    setPipelineStages(withArch);
    try {
      await updatePipelineStages(withArch);
    } catch (error) {
      setPipelineStages(previous);
      throw error;
    }
  };

  const deleteStage = async (name) => {
    if (name === ARCHIVED_STAGE) return;
    const previous = pipelineStages;
    const current = pipelineStages.filter((s) => s && s !== ARCHIVED_STAGE && s !== name);
    const withArch = [...current, ARCHIVED_STAGE];
    setPipelineStages(withArch);
    try {
      await updatePipelineStages(withArch);
    } catch (error) {
      setPipelineStages(previous);
      throw error;
    }
  };

  const reorderStages = async (newOrder) => {
    const previous = pipelineStages;
    const cleaned = newOrder.filter((s) => s && s !== ARCHIVED_STAGE);
    const withArch = [...cleaned, ARCHIVED_STAGE];
    setPipelineStages(withArch);
    try {
      await updatePipelineStages(withArch);
    } catch (error) {
      setPipelineStages(previous);
      throw error;
    }
  };

  const value = {
    pipelineStages,
    isSettingsLoading,
    updatePipelineStages,
    ensureStageDocExists,
    addStage,
    renameStage,
    deleteStage,
    reorderStages,
    ARCHIVED_STAGE,
  };

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
};

export default SettingsContext;
